import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {getDashboardWorth} from '../../api/ledger';
import {findAllMovements} from '../../api/moneyMovement';
import {selectAccountsById, selectAccountsInitialized} from '../../store/accounts';
import {selectAccountsSorted} from '../../store/selectors';
import {selectCurrencyRates} from '../../store/currencyRates';
import Loader from '../../components/common/Loader';
import TransactionItem from '../TransactionsPage/components/TransactionItem';
import {groupByDate} from '../TransactionsPage/utils/mathUtils';
import {formatGroupDate} from '../TransactionsPage/utils/dateUtils';
import WorthWidget from './components/WorthWidget';
import PortfolioPieChart from './components/PortfolioPieChart';
import LastMonthWidget from './components/LastMonthWidget';
import ProjectIncomeTable from './components/ProjectIncomeTable';
import DateInput from '../../components/common/DateInput/DateInput';
import './Dashboard.scss';

const pad2 = n => String(n).padStart(2, '0');
const localDateStr = (d) => `${d.getFullYear()}-${pad2(
    d.getMonth() + 1)}-${pad2(d.getDate())}`;

const currentYearMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
};

const computeMonthRange = (yearMonth) => {
    const [y, m] = yearMonth.split('-').map(Number);
    const monthIndex = m - 1;
    const start = new Date(y, monthIndex, 1);
    const end = new Date(y, monthIndex + 1, 0);
    const today = new Date();
    const isCurrentMonth = y === today.getFullYear() && monthIndex ===
        today.getMonth();
    return {
        monthStart: localDateStr(start),
        monthEnd: isCurrentMonth ? localDateStr(today) : localDateStr(end),
    };
};

const shiftMonth = (yearMonth, delta) => {
    const [y, m] = yearMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const Dashboard = () => {
    const accounts = useSelector(selectAccountsSorted);
    const accountsById = useSelector(selectAccountsById);
    const accountsInitialized = useSelector(selectAccountsInitialized);
    const currencyRates = useSelector(selectCurrencyRates);

    const balancesToday = useMemo(
        () => Object.fromEntries(accounts.map(a => [a.id, a.balance ?? 0])),
        [accounts],
    );
    const [worthData, setWorthData] = useState({});
    const [thisMonthMovements, setThisMonthMovements] = useState([]);
    const [loadingTop, setLoadingTop] = useState(true);
    const [loadingMovements, setLoadingMovements] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);

    const maxMonth = currentYearMonth();

    // Effect A: top section — single worth summary call (per-currency totals, delta, sparkline)
    useEffect(() => {
        if (!accounts.length) {
            setLoadingTop(false);
            return;
        }

        const now = new Date();
        const thisMonthStart = localDateStr(
            new Date(now.getFullYear(), now.getMonth(), 1));

        getDashboardWorth(thisMonthStart)
            .then(setWorthData)
            .finally(() => setLoadingTop(false));
    }, [accounts]); // eslint-disable-line react-hooks/exhaustive-deps

    // Effect B: bottom sections — movements for selected month
    useEffect(() => {
        if (!accounts.length) {
            setLoadingMovements(false);
            return;
        }

        setLoadingMovements(true);
        const {monthStart, monthEnd} = computeMonthRange(selectedMonth);

        findAllMovements({from: monthStart, to: monthEnd})
            .then(setThisMonthMovements)
            .finally(() => setLoadingMovements(false));
    }, [accounts, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!accountsInitialized) return <Loader fullScreen/>;

    function renderDatePicker() {
        return (<div className="dashboard__month-picker">
            <button onClick={() => setSelectedMonth(m => shiftMonth(m, -1))}>‹
            </button>
            <DateInput
                mode="month"
                value={selectedMonth}
                max={maxMonth}
                onChange={v => v && setSelectedMonth(v)}
            />
            <button
                onClick={() => setSelectedMonth(m => shiftMonth(m, 1))}
                disabled={selectedMonth >= maxMonth}
            >›
            </button>
        </div>);
    }

    function renderTop() {
        return <>
            {loadingTop
                ? <div className="dashboard__top-loader"><Loader/></div>
                : <>
                    <WorthWidget worthData={worthData}/>

                    <PortfolioPieChart
                        accounts={accounts}
                        balancesToday={balancesToday}
                        currencyRates={currencyRates}
                    />
                </>
            }
        </>;
    }

    function renderMonthStats() {
        if (loadingMovements && loadingTop) {
            return (<Loader/>);
        }

        if (loadingMovements) {
            return (<>
                {renderDatePicker()}
                <Loader/>
            </>);
        }

        return (<>
            {renderDatePicker()}
            <LastMonthWidget
                movements={thisMonthMovements}
                accounts={accounts}
                selectedMonth={selectedMonth}
            />

            <section className="dashboard__section">
                <h3 className="dashboard__section-title">Income by project</h3>
                <ProjectIncomeTable
                    movements={thisMonthMovements}
                    currencyRates={currencyRates}
                    selectedMonth={selectedMonth}
                />
            </section>

            <section className="dashboard__section">
                <h3 className="dashboard__section-title">Month activity</h3>
                {thisMonthMovements.length === 0
                    ? <p className="dashboard__empty">No activity yet.</p>
                    : <div className="dashboard__activity">
                        {groupByDate(thisMonthMovements).map(group => (
                            <div key={group.date} className="tx-date-group">
                                <div
                                    className="tx-date-header">{formatGroupDate(
                                    group.date)}</div>
                                {group.items.map(item => (
                                    <TransactionItem
                                        key={`${item.type}-${item.id}`}
                                        item={item}
                                        accountsById={accountsById}/>
                                ))}
                            </div>
                        ))}
                    </div>
                }
            </section>
        </>);
    }

    return (
        <div className="capital-page">
            <div className="capital-page__body">
                <div className="vertically-scrollable dashboard">
                    {renderTop()}
                    {renderMonthStats()}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
